import { type AiMessage } from "@/providers/AiAssistantProvider";

/**
 * A consecutive run of tool rows (collapsed into one toggle when long) or a
 * single user/assistant bubble. Tool runs are merged ACROSS the empty
 * tool-chaining assistant bubbles the reducer opens after every tool_use —
 * those render nothing anyway (MessageBubble collapses them), so they must not
 * split an otherwise-contiguous run of steps.
 */
export type TranscriptItem =
  | { kind: "message"; message: AiMessage; index: number }
  | { kind: "tools"; tools: AiMessage[] };

/** An empty, finalized, error-free assistant bubble — the artifact the reducer
 *  opens after every tool_use. MessageBubble renders it as null (unless it is
 *  the genuine last bubble, which carries the "Stopped" marker). */
function isChainGap(m: AiMessage): boolean {
  return m.role === "assistant" && !m.streaming && !m.content && !m.errorCode;
}

export function groupTranscript(messages: AiMessage[]): TranscriptItem[] {
  const items: TranscriptItem[] = [];
  const lastIndex = messages.length - 1;
  let run: AiMessage[] = [];
  // A chain gap that carries a PRICE is not an artifact: when a turn goes
  // straight to a tool with no preamble, the end-of-turn carry lands the
  // message's total on exactly such a bubble. It must render — but emitting it
  // where it sits would split the tool run around it, turning one collapsible
  // "N steps" into N flat rows. So it is held back and emitted after the run,
  // which is also where a turn's price belongs: at its end.
  let deferredPrice: AiMessage | null = null;
  let deferredIndex = -1;

  const flushTools = () => {
    if (run.length) items.push({ kind: "tools", tools: run });
    run = [];
  };

  messages.forEach((m, i) => {
    if (m.role === "tool") {
      run.push(m);
      return;
    }
    // `preview` rows are dead (no reducer action creates them); drop silently.
    if (m.role === "preview") return;
    // Skip invisible chaining artifacts so they don't split a tool run — but
    // keep the genuine LAST stopped bubble (it carries the "Stopped" marker).
    if (isChainGap(m) && i !== lastIndex) {
      // Prices are cumulative, so a later gap supersedes an earlier one.
      if (typeof m.creditCost === "number") {
        deferredPrice = m;
        deferredIndex = i;
      }
      return;
    }
    flushTools();
    // Any message that actually renders supersedes a held-back price: an
    // assistant bubble with text carries the turn's stamp itself, and a user
    // bubble means the turn moved on.
    deferredPrice = null;
    items.push({ kind: "message", message: m, index: i });
  });
  flushTools();
  if (deferredPrice) {
    items.push({ kind: "message", message: deferredPrice, index: deferredIndex });
  }
  return items;
}
